/**
 * Utility functions for formatting data
 */

/**
 * Format price from cents to dollars
 * @param priceInCents - Price in cents (e.g., 4999 = $49.99)
 * @returns Formatted price string (e.g., "49.99")
 */
export function formatPrice(priceInCents: number): string {
  return (priceInCents / 100).toFixed(2);
}

/**
 * Format price with currency symbol
 * @param priceInCents - Price in cents
 * @param currency - Currency symbol (default: "$")
 * @returns Formatted price with symbol (e.g., "$49.99")
 */
export function formatPriceWithCurrency(priceInCents: number, currency: string = '$'): string {
  return `${currency}${formatPrice(priceInCents)}`;
}

/**
 * Calculate savings amount
 * @param originalPrice - Original price in cents
 * @param salePrice - Sale price in cents
 * @returns Savings amount in cents
 */
export function calculateSavings(originalPrice: number, salePrice: number): number {
  return originalPrice - salePrice;
}

/**
 * Calculate discount percentage
 * @param originalPrice - Original price in cents
 * @param salePrice - Sale price in cents
 * @returns Discount percentage (e.g., 25 for 25% off)
 */
export function calculateDiscountPercent(originalPrice: number, salePrice: number): number {
  if (originalPrice === 0) return 0;
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
}

/**
 * Truncate text to specified length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to add when truncated (default: "...")
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Format phone number
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

/**
 * Format make/brand name to uppercase
 * @param makeData - Make/brand name (string) or make object with title/name properties
 * @returns Uppercase make/brand name, or empty string if invalid
 */
export function formatMakeName(
  makeData: string | { title?: string; name?: string; [key: string]: any } | null | undefined
): string {
  if (!makeData) return '';

  if (typeof makeData === 'string') {
    return makeData.toUpperCase();
  }

  if (typeof makeData === 'object') {
    const name = makeData.title || makeData.name || '';
    return name.toUpperCase();
  }

  return '';
}

/**
 * Deduplicate models by their unique identifier.
 *
 * The API returns one row per model+engine fitment, and the same physical
 * model can be split across several differently-formatted `name` values
 * left over from past catalog imports (e.g. "Silverado 1500",
 * "silverado-1500", "silverado_1500" all mean the same model). `slug` is
 * consistent across those variants, so dedupe on that when present and
 * only fall back to `name` for data that predates/lacks a slug.
 * Preserves the first occurrence's title for display.
 * @param models - Array of model objects with name/title (and ideally slug)
 * @returns Deduplicated array with unique models
 */
export function deduplicateModels<T extends { name: string; title: string; slug?: string }>(
  models: T[] | undefined | null
): T[] {
  if (!models || !Array.isArray(models)) {
    return [];
  }

  const seen = new Set<string>();
  const deduplicated: T[] = [];

  for (const model of models) {
    const key = model.slug || model.name;
    if (!seen.has(key)) {
      seen.add(key);
      deduplicated.push(model);
    }
  }

  return deduplicated;
}

/**
 * Append a unit to a bare numeric measurement from the catalog.
 * Values that already carry a unit (e.g. '2.00"', '45°') are returned as-is.
 * @param value - Raw catalog value, e.g. "1.4429"
 * @param unit - Unit suffix, e.g. ' in' or '°'
 */
export function formatMeasurement(value: string | undefined, unit: string): string | undefined {
  if (!value) return value;
  const trimmed = String(value).trim();
  return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}${unit}` : trimmed;
}
